import * as React from 'react'
import { Modal, Descriptions, Icon, message } from 'antd'
import { CashCounterTable } from '../../../POS/CashCount/CashCounterTable'
import { GrPrice, parseErrorMsg } from '../../../../utilities/helpers'
import { IDevice } from '@getgreenline/homi-shared'
import { observer } from 'mobx-react-lite'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { inject } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { ShiftModels } from '@getgreenline/shifts'

interface Props {
  selectedShift: ShiftModels.IBaseShift
  selectedDevice: IDevice
  visible: boolean
  setIsCashCounterModalVisible: (visible: boolean) => void
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

const createCashCount = new ShiftModels.CreateCashCount()

export const DashboardCashCountModal = inject(
  'currentCompanyStore',
  'currentUserStore',
)(
  observer((props: Props & InjectedProps) => {
    const {
      selectedShift,
      selectedDevice,
      visible,
      setIsCashCounterModalVisible,
      currentCompanyStore,
      currentUserStore,
    } = props

    const currentUserId = currentUserStore!.currentUser!.id
    const cashCount = selectedShift.cashCount || createCashCount.cashCount
    const expectedCashCount = selectedShift.endCash

    const [isViewOnly, setIsViewOnly] = React.useState(selectedShift.cashCount !== null)
    const [isSubmitting, setIsSubmitting] = React.useState(false)

    React.useEffect(() => {
      setIsViewOnly(selectedShift.cashCount !== null)
    }, [selectedShift])

    const updateCashCount = async () => {
      try {
        if (createCashCount.cashCount === null) {
          return
        }

        setIsSubmitting(true)

        await currentCompanyStore!.updateCashCount(selectedDevice.id, selectedShift.id, {
          cashCount: createCashCount.cashCount,
          userId: currentUserId,
        })

        selectedShift.cashCount = createCashCount.cashCount
        createCashCount.setCashCount('default')

        setIsViewOnly(true)

        message.success('Cash count updated')
      } catch (error) {
        message.error(parseErrorMsg(error) || 'An error occurred while submitting the cash count')
      } finally {
        setIsSubmitting(false)
      }
    }

    const getTotalCashCount = () => {
      let total = 0

      if (cashCount === null) {
        return total
      } else {
        ShiftModels.acceptableDenominations.forEach((deno) => {
          const count = cashCount![deno] || 0
          total += deno * count
        })
      }

      return total || 0
    }

    const onInputNumberChange = (coinDeno: number, value?: number | string | null) => {
      let count

      if (value === undefined || value === null || value === '') {
        count = null
      } else {
        const stringValue = value.toString().replace(/\D/g, '')
        count = parseInt(stringValue)
      }

      createCashCount.setCashCountItem(coinDeno, count)
    }

    const discrepancy = getTotalCashCount() - expectedCashCount

    return (
      <Modal
        title={`Shift ID: ${selectedShift.id}`}
        visible={visible}
        maskClosable={false}
        onCancel={() => setIsCashCounterModalVisible(false)}
        footer={
          isViewOnly
            ? false
            : [
                <BlazeButton
                  key='close'
                  buttonProps={{
                    type: 'danger',
                    onClick: () => setIsCashCounterModalVisible(false),
                  }}
                  titleProps={{ title: 'Close' }}
                />,
                <BlazeButton
                  key='submit'
                  buttonProps={{
                    type: 'primary',
                    loading: isSubmitting,
                    disabled: !createCashCount.isCashCountFilled,
                    onClick: updateCashCount,
                  }}
                  titleProps={{
                    title: createCashCount.isCashCountFilled ? 'Submit' : 'Count required',
                  }}
                />,
              ]
        }
      >
        {cashCount && (
          <CashCounterTable
            disabled={isViewOnly}
            cashCount={cashCount}
            onInputNumberChange={onInputNumberChange}
          />
        )}

        <Descriptions bordered size='small' column={1}>
          {isViewOnly && (
            <Descriptions.Item label='Expected'>
              {GrPrice.convertCentToDollar(expectedCashCount)}
            </Descriptions.Item>
          )}

          <Descriptions.Item label='Counted'>
            {GrPrice.convertCentToDollar(getTotalCashCount())}
          </Descriptions.Item>

          {isViewOnly && (
            <Descriptions.Item label='Discrepancy'>
              <span
                className={`d-flex align-items-center font-weight-bold ${
                  discrepancy === 0 ? 'text-success' : 'text-danger'
                }`}
              >
                <span className='mr-2'>{GrPrice.convertCentToDollar(discrepancy)}</span>
              </span>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Modal>
    )
  }),
)
